// Season 2 achievement icons - base images with level-based color filters
import { AchievementType } from '@/types/achievements'

// S2 base images - one per achievement type
import FriendsReferredIcon from '@/assets/images/achievements/s2/friends-referred.webp'
import MysteryBoxIcon from '@/assets/images/achievements/s2/mystery-box.webp'
import DaysOnDropeeIcon from '@/assets/images/achievements/s2/days-on-dropee.webp'
import OnChainTransactionsIcon from '@/assets/images/achievements/s2/on-chain-transactions.webp'
import BadgesCollectedIcon from '@/assets/images/achievements/s2/badges-collected.webp'
import CardsIcon from '@/assets/images/achievements/s2/cards.webp'
import DailyCheckinIcon from '@/assets/images/achievements/s2/daily-checkin.webp'
import DailyComboIcon from '@/assets/images/achievements/s2/daily-combo.webp'
import PathwayStepsClaimedIcon from '@/assets/images/achievements/s2/pathway-steps-claimed.webp'
import QuestsCompletedIcon from '@/assets/images/achievements/s2/quests-completed.webp'
import WheelSpinsIcon from '@/assets/images/achievements/s2/wheel-spins.webp'
import PathwaysCompletedIcon from '@/assets/images/achievements/s2/pathways-completed.webp'

// Season 2 achievement item with level-based coloring
export type Season2AchievementItem = {
  number: number,
  label?: string,
  level: number,  // 1-11 milestone level for color filtering
  percentage?: number,
  tooltip?: string
}

// Achievement type to base icon mapping
export const achievementTypeIconMap: Record<AchievementType, string> = {
  [AchievementType.Friends]: FriendsReferredIcon,
  [AchievementType.MysteryBoxes]: MysteryBoxIcon,
  [AchievementType.DaysSinceOnboarding]: DaysOnDropeeIcon,
  [AchievementType.OnChainTransactions]: OnChainTransactionsIcon,
  [AchievementType.Badges]: BadgesCollectedIcon,
  [AchievementType.CardsUpgrade]: CardsIcon,
  [AchievementType.CardsAbove15]: CardsIcon,
  [AchievementType.CardsAbove25]: CardsIcon,
  [AchievementType.CardsAbove50]: CardsIcon,
  [AchievementType.CardsAbove100]: CardsIcon,
  [AchievementType.CardsAbove150]: CardsIcon,
  [AchievementType.SpecialCards]: CardsIcon,
  [AchievementType.DailyCheckinStreaks]: DailyCheckinIcon,
  [AchievementType.DailyCombo]: DailyComboIcon,
  [AchievementType.PathwaySteps]: PathwayStepsClaimedIcon,
  [AchievementType.Tasks]: QuestsCompletedIcon,
  [AchievementType.Spins]: WheelSpinsIcon,
  [AchievementType.Pathways]: PathwaysCompletedIcon,
}

// Level colors - hex colors at 44% opacity
export const levelColors: Record<number, string> = {
  1: '#CD7F32',   // Bronze
  2: '#C0C0C0',   // Silver
  3: '#FFD700',   // Gold
  4: '#50C878',   // Emerald
  5: '#E0115F',   // Ruby
  6: '#0F52BA',   // Sapphire
  7: '#AA4069',   // Mauve
  8: '#9966CC',   // Amethyst
  9: '#E5E4E2',   // Platinum
  10: '#B9F2FF',  // Diamond
  11: '#E9D0EC', // Pink Diamond
}

// Season 2 milestone configurations (level 1-11, max 11 milestones per type)
export const season2AchievementCategories: Record<AchievementType, {
  milestones: Season2AchievementItem[],
}> = {
  [AchievementType.DaysSinceOnboarding]: {
    milestones: [
      { number: 1, level: 1, percentage: 95, tooltip: '{percentage} of players have reached this achievement!' },
      { number: 7, level: 2, percentage: 71, tooltip: 'You joined {percentage} of players! Keep going to reach the higher ranks!' },
      { number: 15, level: 3, percentage: 64, tooltip: 'You\'re making progress-only {percentage} of players have unlocked this!' },
      { number: 30, level: 4, percentage: 49, tooltip: 'Halfway to the top! You\'re in the top {percentage} of players !' },
      { number: 60, level: 5, percentage: 36, tooltip: 'Only {percentage} have reached this milestone!' },
      { number: 90, level: 6, percentage: 25, tooltip: 'Top {percentage} of Dropee\'s finest! You\'re climbing fast!' },
      { number: 120, level: 7, percentage: 16, tooltip: 'Elite tier-only {percentage} of players have achieved this. Great work!' },
      { number: 150, level: 8, percentage: 9, tooltip: 'You\'re among the top {percentage}! A true master!' },
      { number: 190, level: 9, percentage: 4, tooltip: 'Incredible-just {percentage} of players have unlocked this achievement!' },
      { number: 220, level: 10, percentage: 1, tooltip: 'Legendary status! You\'re among the top {percentage}!' },
      { number: 250, level: 11 },
    ],
  },
  [AchievementType.DailyCheckinStreaks]: {
    milestones: [
      { number: 5, level: 1, percentage: 95, tooltip: '{percentage} of players have reached this achievement!' },
      { number: 10, level: 2, percentage: 71, tooltip: 'You joined {percentage} of players! Keep going to reach the higher ranks!' },
      { number: 25, level: 3, percentage: 64, tooltip: 'You\'re making progress-only {percentage} of players have unlocked this!' },
      { number: 50, level: 4, percentage: 49, tooltip: 'Halfway to the top! You\'re in the top {percentage} of players !' },
      { number: 100, level: 5, percentage: 36, tooltip: 'Only {percentage} have reached this milestone!' },
      { number: 120, level: 6, percentage: 25, tooltip: 'Top {percentage} of Dropee\'s finest! You\'re climbing fast!' },
      { number: 140, level: 7, percentage: 16, tooltip: 'Elite tier-only {percentage} of players have achieved this. Great work!' },
      { number: 160, level: 8, percentage: 9, tooltip: 'You\'re among the top {percentage}! A true master!' },
      { number: 180, level: 9, percentage: 4, tooltip: 'Incredible-just {percentage} of players have unlocked this achievement!' },
      { number: 200, level: 10, percentage: 1, tooltip: 'Legendary status! You\'re among the top {percentage}!' },
      { number: 250, level: 11 },
    ],
  },
  [AchievementType.CardsUpgrade]: {
    milestones: [
      { number: 10, level: 1, percentage: 95, tooltip: '{percentage} of players have reached this achievement!' },
      { number: 100, level: 2, percentage: 71, tooltip: 'You joined {percentage} of players! Keep going to reach the higher ranks!' },
      { number: 250, level: 3, percentage: 64, tooltip: 'You\'re making progress-only {percentage} of players have unlocked this!' },
      { number: 650, level: 4, percentage: 49, tooltip: 'Halfway to the top! You\'re in the top {percentage} of players !' },
      { number: 1000, level: 5, percentage: 36, tooltip: 'Only {percentage} have reached this milestone!' },
      { number: 1350, level: 6, percentage: 25, tooltip: 'Top {percentage} of Dropee\'s finest! You\'re climbing fast!' },
      { number: 1600, level: 7, percentage: 16, tooltip: 'Elite tier-only {percentage} of players have achieved this. Great work!' },
      { number: 1900, level: 8, percentage: 9, tooltip: 'You\'re among the top {percentage}! A true master!' },
      { number: 2400, level: 9, percentage: 4, tooltip: 'Incredible-just {percentage} of players have unlocked this achievement!' },
      { number: 3000, level: 10, percentage: 1, tooltip: 'Legendary status! You\'re among the top {percentage}!' },
      { number: 5000, level: 11 },
    ],
  },
  [AchievementType.CardsAbove15]: {
    milestones: [
      { number: 3, level: 1, percentage: 95, tooltip: '{percentage} of players have reached this achievement!' },
      { number: 5, level: 2, percentage: 71, tooltip: 'You joined {percentage} of players! Keep going to reach the higher ranks!' },
      { number: 7, level: 3, percentage: 64, tooltip: 'You\'re making progress-only {percentage} of players have unlocked this!' },
      { number: 10, level: 4, percentage: 49, tooltip: 'Halfway to the top! You\'re in the top {percentage} of players !' },
      { number: 15, level: 5, percentage: 36, tooltip: 'Only {percentage} have reached this milestone!' },
      { number: 20, level: 6, percentage: 25, tooltip: 'Top quarter of Dropee\'s finest! You\'re climbing fast!' },
      { number: 25, level: 7, percentage: 16, tooltip: 'Elite tier-only {percentage} of players have achieved this. Great work!' },
      { number: 30, level: 8, percentage: 9, tooltip: 'You\'re among the top {percentage}! A true master!' },
      { number: 35, level: 9, percentage: 4, tooltip: 'Incredible-just {percentage} of players have unlocked this achievement!' },
      { number: 50, level: 10 },
      { number: 75, level: 11 },
    ],
  },
  [AchievementType.CardsAbove25]: {
    milestones: [
      { number: 1, level: 1, percentage: 95, tooltip: '{percentage} of players have reached this achievement!' },
      { number: 2, level: 2, percentage: 71, tooltip: 'You joined {percentage} of players! Keep going to reach the higher ranks!' },
      { number: 3, level: 3, percentage: 64, tooltip: 'You\'re making progress-only {percentage} of players have unlocked this!' },
      { number: 4, level: 4, percentage: 49, tooltip: 'Halfway to the top! You\'re in the top {percentage} of players !' },
      { number: 5, level: 5, percentage: 36, tooltip: 'Only {percentage} have reached this milestone!' },
      { number: 6, level: 6, percentage: 25, tooltip: 'Top quarter of Dropee\'s finest! You\'re climbing fast!' },
      { number: 10, level: 7, percentage: 16, tooltip: 'Elite tier-only {percentage} of players have achieved this. Great work!' },
      { number: 15, level: 8, percentage: 9, tooltip: 'You\'re among the top {percentage}! A true master!' },
      { number: 21, level: 9, percentage: 4, tooltip: 'Incredible-just {percentage} of players have unlocked this achievement!' },
      { number: 29, level: 10, percentage: 1, tooltip: 'Legendary status! You\'re among the top {percentage}!' },
      { number: 50, level: 11 },
    ],
  },
  [AchievementType.CardsAbove50]: {
    milestones: [
      { number: 1, level: 1, percentage: 95, tooltip: '{percentage} of players have reached this achievement!' },
      { number: 3, level: 2, percentage: 71, tooltip: 'You joined {percentage} of players! Keep going to reach the higher ranks!' },
      { number: 5, level: 3, percentage: 64, tooltip: 'You\'re making progress-only {percentage} of players have unlocked this!' },
      { number: 7, level: 4, percentage: 49, tooltip: 'Halfway to the top! You\'re in the top {percentage} of players !' },
      { number: 10, level: 5, percentage: 36, tooltip: 'Only {percentage} have reached this milestone!' },
      { number: 15, level: 6, percentage: 25, tooltip: 'Top quarter of Dropee\'s finest! You\'re climbing fast!' },
      { number: 20, level: 7, percentage: 16, tooltip: 'Elite tier-only {percentage} of players have achieved this. Great work!' },
      { number: 25, level: 8, percentage: 9, tooltip: 'You\'re among the top {percentage}! A true master!' },
      { number: 35, level: 9, percentage: 4, tooltip: 'Incredible-just {percentage} of players have unlocked this achievement!' },
      { number: 50, level: 10, percentage: 1, tooltip: 'Legendary status! You\'re among the top {percentage}!' },
      { number: 75, level: 11 },
    ],
  },
  [AchievementType.CardsAbove100]: {
    milestones: [
      { number: 1, level: 1, percentage: 95, tooltip: '{percentage} of players have reached this achievement!' },
      { number: 2, level: 2, percentage: 71, tooltip: 'You joined {percentage} of players! Keep going to reach the higher ranks!' },
      { number: 3, level: 3, percentage: 64, tooltip: 'You\'re making progress-only {percentage} of players have unlocked this!' },
      { number: 5, level: 4, percentage: 49, tooltip: 'Halfway to the top! You\'re in the top {percentage} of players !' },
      { number: 7, level: 5, percentage: 36, tooltip: 'Only {percentage} have reached this milestone!' },
      { number: 10, level: 6, percentage: 25, tooltip: 'Top quarter of Dropee\'s finest! You\'re climbing fast!' },
      { number: 15, level: 7, percentage: 16, tooltip: 'Elite tier-only {percentage} of players have achieved this. Great work!' },
      { number: 20, level: 8, percentage: 9, tooltip: 'You\'re among the top {percentage}! A true master!' },
      { number: 25, level: 9, percentage: 4, tooltip: 'Incredible-just {percentage} of players have unlocked this achievement!' },
      { number: 35, level: 10, percentage: 1, tooltip: 'Legendary status! You\'re among the top {percentage}!' },
      { number: 50, level: 11 },
    ],
  },
  [AchievementType.CardsAbove150]: {
    milestones: [
      { number: 1, level: 1, percentage: 95, tooltip: '{percentage} of players have reached this achievement!' },
      { number: 2, level: 2, percentage: 71, tooltip: 'You joined {percentage} of players! Keep going to reach the higher ranks!' },
      { number: 3, level: 3, percentage: 64, tooltip: 'You\'re making progress-only {percentage} of players have unlocked this!' },
      { number: 5, level: 4, percentage: 49, tooltip: 'Halfway to the top! You\'re in the top {percentage} of players !' },
      { number: 7, level: 5, percentage: 36, tooltip: 'Only {percentage} have reached this milestone!' },
      { number: 10, level: 6, percentage: 25, tooltip: 'Top quarter of Dropee\'s finest! You\'re climbing fast!' },
      { number: 15, level: 7, percentage: 16, tooltip: 'Elite tier-only {percentage} of players have achieved this. Great work!' },
      { number: 20, level: 8, percentage: 9, tooltip: 'You\'re among the top {percentage}! A true master!' },
      { number: 25, level: 9, percentage: 4, tooltip: 'Incredible-just {percentage} of players have unlocked this achievement!' },
      { number: 35, level: 10, percentage: 1, tooltip: 'Legendary status! You\'re among the top {percentage}!' },
      { number: 50, level: 11 },
    ],
  },
  [AchievementType.SpecialCards]: {
    milestones: [
      { number: 1, level: 1, percentage: 95, tooltip: '{percentage} of players have reached this achievement!' },
      { number: 2, level: 2, percentage: 71, tooltip: 'You joined {percentage} of players! Keep going to reach the higher ranks!' },
      { number: 3, level: 3, percentage: 64, tooltip: 'You\'re making progress-only {percentage} of players have unlocked this!' },
      { number: 4, level: 4, percentage: 49, tooltip: 'Halfway to the top! You\'re in the top {percentage} of players !' },
      { number: 5, level: 5, percentage: 36, tooltip: 'Only {percentage} have reached this milestone!' },
      { number: 10, level: 6, percentage: 25, tooltip: 'Top quarter of Dropee\'s finest! You\'re climbing fast!' },
      { number: 13, level: 7, percentage: 16, tooltip: 'Elite tier-only {percentage} of players have achieved this. Great work!' },
      { number: 15, level: 8, percentage: 9, tooltip: 'You\'re among the top {percentage}! A true master!' },
      { number: 17, level: 9, percentage: 4, tooltip: 'Incredible-just {percentage} of players have unlocked this achievement!' },
      { number: 20, level: 10, percentage: 1, tooltip: 'Legendary status! You\'re among the top {percentage}!' },
      { number: 25, level: 11 },
    ],
  },
  [AchievementType.DailyCombo]: {
    milestones: [
      { number: 1, level: 1 },
      { number: 7, level: 2 },
      { number: 15, level: 3 },
      { number: 30, level: 4 },
      { number: 60, level: 5 },
      { number: 90, level: 6 },
      { number: 120, level: 7 },
      { number: 150, level: 8 },
      { number: 190, level: 9 },
      { number: 220, level: 10 },
      { number: 250, level: 11 },
    ],
  },
  [AchievementType.Friends]: {
    milestones: [
      { number: 1, level: 1, percentage: 95 },
      { number: 3, level: 2, percentage: 71 },
      { number: 5, level: 3, percentage: 64 },
      { number: 10, level: 4, percentage: 49 },
      { number: 15, level: 5, percentage: 36 },
      { number: 20, level: 6, percentage: 25 },
      { number: 30, level: 7, percentage: 16 },
      { number: 40, level: 8, percentage: 9 },
      { number: 50, level: 9, percentage: 4 },
      { number: 100, level: 10, percentage: 1 },
      { number: 200, level: 11 },
    ],
  },
  [AchievementType.Spins]: {
    milestones: [
      { number: 10, level: 1, percentage: 95, tooltip: '{percentage} of players have reached this achievement!' },
      { number: 50, level: 2, percentage: 71, tooltip: 'You joined {percentage} of players! Keep going to reach the higher ranks!' },
      { number: 100, level: 3, percentage: 64, tooltip: 'You\'re making progress-only {percentage} of players have unlocked this!' },
      { number: 500, level: 4, percentage: 49, tooltip: 'Halfway to the top! You\'re in the top {percentage} of players !' },
      { number: 1000, level: 5, percentage: 36, tooltip: 'Only {percentage} have reached this milestone!' },
      { number: 5000, level: 6, percentage: 25, tooltip: 'Top {percentage} of Dropee\'s finest! You\'re climbing fast!' },
      { number: 10000, level: 7, percentage: 16, tooltip: 'Elite tier-only {percentage} of players have achieved this. Great work!' },
      { number: 20000, level: 8, percentage: 9, tooltip: 'You\'re among the top {percentage}! A true master!' },
      { number: 50000, level: 9, percentage: 4, tooltip: 'Incredible-just {percentage} of players have unlocked this achievement!' },
      { number: 100000, level: 10, percentage: 1, tooltip: 'Legendary status! You\'re among the top {percentage}!' },
      { number: 250000, level: 11 },
    ],
  },
  [AchievementType.Tasks]: {
    milestones: [
      { number: 10, level: 1, tooltip: '{percentage} of players have reached this achievement!' },
      { number: 50, level: 2, tooltip: 'You joined {percentage} of players! Keep going to reach the higher ranks!' },
      { number: 100, level: 3, tooltip: 'You\'re making progress-only {percentage} of players have unlocked this!' },
      { number: 200, level: 4, tooltip: 'Halfway to the top! You\'re in the top {percentage} of players !' },
      { number: 250, level: 5, tooltip: 'Only {percentage} have reached this milestone!' },
      { number: 500, level: 6, tooltip: 'Top {percentage} of Dropee\'s finest! You\'re climbing fast!' },
      { number: 750, level: 7, tooltip: 'Elite tier-only {percentage} of players have achieved this. Great work!' },
      { number: 1000, level: 8, tooltip: 'You\'re among the top {percentage}! A true master!' },
      { number: 1250, level: 9, tooltip: 'Incredible-just {percentage} of players have unlocked this achievement!' },
      { number: 1500, level: 10, tooltip: 'Legendary status! You\'re among the top {percentage}!' },
      { number: 2000, level: 11 },
    ],
  },
  [AchievementType.OnChainTransactions]: {
    milestones: [
      { number: 1, level: 1, tooltip: '{percentage} of players have reached this achievement!' },
      { number: 3, level: 2, tooltip: 'You joined {percentage} of players! Keep going to reach the higher ranks!' },
      { number: 5, level: 3, tooltip: 'You\'re making progress-only {percentage} of players have unlocked this!' },
      { number: 10, level: 4, tooltip: 'Halfway to the top! You\'re in the top {percentage} of players !' },
      { number: 15, level: 5, tooltip: 'Only {percentage} have reached this milestone!' },
      { number: 25, level: 6, tooltip: 'Top {percentage} of Dropee\'s finest! You\'re climbing fast!' },
      { number: 50, level: 7, tooltip: 'Elite tier-only {percentage} of players have achieved this. Great work!' },
      { number: 100, level: 8, tooltip: 'You\'re among the top {percentage}! A true master!' },
      { number: 250, level: 9, tooltip: 'Incredible-just {percentage} of players have unlocked this achievement!' },
      { number: 500, level: 10, tooltip: 'Legendary status! You\'re among the top {percentage}!' },
      { number: 1000, level: 11 },
    ],
  },
  [AchievementType.Badges]: {
    milestones: [
      { number: 1, level: 1 },
      { number: 2, level: 2 },
      { number: 3, level: 3 },
      { number: 4, level: 4 },
      { number: 5, level: 5 },
      { number: 8, level: 6 },
      { number: 10, level: 7 },
      { number: 15, level: 8 },
      { number: 20, level: 9 },
      { number: 25, level: 10 },
      { number: 30, level: 11 },
    ],
  },
  [AchievementType.Pathways]: {
    milestones: [
      { number: 1, level: 1 },
      { number: 5, level: 2 },
      { number: 10, level: 3 },
      { number: 25, level: 4 },
      { number: 50, level: 5 },
      { number: 100, level: 6 },
      { number: 250, level: 7 },
      { number: 500, level: 8 },
      { number: 1000, level: 9 },
      { number: 1500, level: 10 },
      { number: 2000, level: 11 },
    ],
  },
  [AchievementType.PathwaySteps]: {
    milestones: [
      { number: 5, level: 1 },
      { number: 10, level: 2 },
      { number: 25, level: 3 },
      { number: 50, level: 4 },
      { number: 100, level: 5 },
      { number: 250, level: 6 },
      { number: 500, level: 7 },
      { number: 1000, level: 8 },
      { number: 2500, level: 9 },
      { number: 5000, level: 10 },
      { number: 7500, level: 11 },
    ],
  },
  [AchievementType.MysteryBoxes]: {
    milestones: [
      { number: 1, level: 1 },
      { number: 5, level: 2 },
      { number: 10, level: 3 },
      { number: 25, level: 4 },
      { number: 50, level: 5 },
      { number: 100, level: 6 },
      { number: 200, level: 7 },
      { number: 350, level: 8 },
      { number: 500, level: 9 },
      { number: 750, level: 10 },
      { number: 1000, level: 11 },
    ],
  },
}

// Get milestone for Season 2 achievements (returns level for color filtering)
export function getSeason2Milestone(achievementType: AchievementType, value: number) {
  return season2AchievementCategories[achievementType].milestones.reduce<{
    number: number;
    level: number;
  }>(
    (last, milestone) =>
      milestone.number <= value ? { number: milestone.number, level: milestone.level } : last,
    { number: 0, level: season2AchievementCategories[achievementType].milestones[0].level },
  )
}
